import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import campaignService from '../../../services/campaign.service';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import './PastBlastList.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const IN_PROGRESS_POLL_MS = 8000;

const STATUS_FILTERS = [
    { value: '', label: 'All' },
    { value: 'IN_PROGRESS', label: 'In progress' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'ERROR', label: 'Error' },
];

const STATUS_META = {
    IN_PROGRESS: {
        label: 'In progress',
        className: 'is-progress',
        icon: 'bi-arrow-repeat',
        hint: 'Recipients are being queued',
    },
    PENDING: {
        label: 'Pending',
        className: 'is-pending',
        icon: 'bi-hourglass-split',
        hint: 'Queued and waiting to send',
    },
    COMPLETED: {
        label: 'Completed',
        className: 'is-completed',
        icon: 'bi-check-lg',
        hint: 'Finished sending',
    },
    ERROR: {
        label: 'Error',
        className: 'is-error',
        icon: 'bi-exclamation-lg',
        hint: 'Queueing or sending failed',
    },
};

const formatCount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '0';
    }
    return number.toLocaleString();
};

const truncateText = (value, max = 90) => {
    if (!value) {
        return '';
    }
    const text = String(value).trim();
    if (text.length <= max) {
        return text;
    }
    return `${text.slice(0, max).trim()}…`;
};

const canExploreResults = (status) => status === 'COMPLETED' || status === 'ERROR';
const canStopCampaign = (status) => status === 'PENDING';

function PastBlastList({
    title,
    description,
    icon,
    type,
    category = 'BLAST',
    entityLabel = 'Blast',
    listHeading,
    createPath,
    createLabel = 'Create blast',
    resultsBasePath,
    emptyTitle = 'No blasts found',
    emptyDescription = 'Created blasts will appear here once they are sent.',
    infoText,
}) {
    const entityLower = entityLabel.toLowerCase();
    const panelHeading = listHeading || `Past ${entityLower}s`;
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [showInfo, setShowInfo] = useState(false);
    const [stoppingId, setStoppingId] = useState(null);

    const isSms = type === 'SMS';
    const hasInProgress = useMemo(
        () => campaigns.some((campaign) => campaign.status === 'IN_PROGRESS'),
        [campaigns]
    );

    const fetchCampaigns = useCallback(
        async (currentPage, currentSize, currentStatus, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                setLoading(false);
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const params = {
                    type,
                    category,
                    page: currentPage,
                    size: currentSize,
                };
                if (currentStatus) {
                    params.status = currentStatus;
                }

                const response = await campaignService.getCampaigns(clinicId, params);
                const data = response.data || {};
                const content = data.content || [];

                setCampaigns(content);
                setTotalElements(data.totalElements || 0);
                setTotalPages(data.totalPages || 0);
                setPage(data.number ?? currentPage);
                setSize(data.size ?? currentSize);
            } catch (error) {
                console.error(`Error fetching ${entityLower}s:`, error);
                if (!silent) {
                    toast.error(`Could not load past ${entityLower}s.`);
                }
                setCampaigns([]);
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId, type, category, entityLower]
    );

    useEffect(() => {
        fetchCampaigns(0, size, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clinicId, type, category]);

    useEffect(() => {
        if (!hasInProgress || !clinicId) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            fetchCampaigns(page, size, statusFilter, { silent: true });
        }, IN_PROGRESS_POLL_MS);

        return () => window.clearInterval(timer);
    }, [hasInProgress, clinicId, page, size, statusFilter, fetchCampaigns]);

    const handleStatusFilterChange = (newStatus) => {
        setStatusFilter(newStatus);
        setPage(0);
        fetchCampaigns(0, size, newStatus);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
        fetchCampaigns(0, newSize, statusFilter);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchCampaigns(newPage, size, statusFilter);
    };

    const handleRefresh = async () => {
        if (!clinicId || isRefreshing || loading) {
            return;
        }

        setIsRefreshing(true);
        try {
            await fetchCampaigns(page, size, statusFilter, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleStopCampaign = async (campaign) => {
        if (!clinicId || !campaign?.id || stoppingId) {
            return;
        }

        const result = await Swal.fire({
            title: `Stop this ${entityLower}?`,
            html: `Stop <strong>${campaign.name || `this ${entityLower}`}</strong>? Waiting messages will not be sent, and the ${entityLower} will be marked as Error.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: `Stop ${entityLower}`,
            cancelButtonText: 'Keep running',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'btn btn-danger rounded-pill px-3 me-2',
                cancelButton:
                    'btn btn-outline-secondary rounded-pill px-3 ecx-swal__btn ecx-swal__btn--cancel',
            },
        });

        if (!result.isConfirmed) {
            return;
        }

        setStoppingId(campaign.id);
        try {
            await campaignService.stopCampaign(clinicId, campaign.id);
            toast.success(`${entityLabel} stopped.`);
            await fetchCampaigns(page, size, statusFilter, { silent: true });
        } catch (error) {
            console.error(`Error stopping ${entityLower}:`, error);
            toast.error(
                error?.response?.data?.message || `Could not stop this ${entityLower}.`
            );
        } finally {
            setStoppingId(null);
        }
    };

    const subtitle = useMemo(() => {
        if (!totalElements) {
            return description;
        }
        return `${description} · ${formatCount(totalElements)} total`;
    }, [description, totalElements]);

    return (
        <div className="ecx-page ecx-page--full-width ecx-past-blast-page">
            <SubHeader
                title={title}
                description={subtitle}
                icon={icon}
                containerClass="w-100 px-4"
            >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {infoText && (
                        <button
                            type="button"
                            className={`btn btn-sm ${
                                showInfo ? 'btn-primary' : 'btn-outline-secondary'
                            }`}
                            onClick={() => setShowInfo((prev) => !prev)}
                            aria-expanded={showInfo}
                            aria-controls="ecx-past-blast-info"
                            aria-label={showInfo ? 'Hide tip' : 'Good to know'}
                            title={showInfo ? 'Hide tip' : 'Good to know'}
                        >
                            <i className="bi bi-info-circle" />
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleRefresh}
                        disabled={isRefreshing || loading}
                    >
                        <i
                            className={`bi bi-arrow-clockwise me-1 ${
                                isRefreshing ? 'ecx-refresh-spin' : ''
                            }`}
                        />
                        Refresh
                    </button>
                    {createPath && (
                        <Link to={createPath} className="btn btn-sm btn-primary">
                            <i className="bi bi-plus-lg me-1" />
                            {createLabel}
                        </Link>
                    )}
                </div>
            </SubHeader>

            <div className="ecx-page-body">
                {infoText && showInfo && (
                    <div
                        id="ecx-past-blast-info"
                        className="ecx-past-blast__notice mb-3"
                        role="region"
                        aria-label="Good to know"
                    >
                        <span className="ecx-page-icon-badge flex-shrink-0" aria-hidden="true">
                            <i className="bi bi-info-circle" />
                        </span>
                        <div className="ecx-past-blast__notice-body">
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                                <div className="fw-normal text-dark">Good to know</div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-link text-muted p-0 ecx-past-blast__notice-hide"
                                    onClick={() => setShowInfo(false)}
                                    aria-label="Hide tip"
                                >
                                    <i className="bi bi-x-lg" />
                                </button>
                            </div>
                            <p className="text-muted small mb-0">{infoText}</p>
                        </div>
                    </div>
                )}

                <div className="ecx-page-card ecx-page-panel ecx-past-blast__panel">
                    <div className="ecx-page-panel__header">
                        <div className="ecx-past-blast__toolbar">
                            <div className="ecx-past-blast__toolbar-meta">
                                <h5 className="mb-0 fw-normal text-dark">{panelHeading}</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul" />
                                    {formatCount(totalElements)} total
                                </span>
                            </div>

                            <div className="ecx-past-blast__toolbar-actions">
                                <div
                                    className="ecx-past-blast__status-pills"
                                    role="tablist"
                                    aria-label="Status filter"
                                >
                                    {STATUS_FILTERS.map((filter) => (
                                        <button
                                            key={filter.value || 'all'}
                                            type="button"
                                            role="tab"
                                            aria-selected={statusFilter === filter.value}
                                            className={`ecx-past-blast__pill ${
                                                statusFilter === filter.value ? 'is-active' : ''
                                            }`}
                                            onClick={() => handleStatusFilterChange(filter.value)}
                                            disabled={loading}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>

                                <label className="ecx-past-blast__page-size mb-0 small text-muted">
                                    <span>Show</span>
                                    <select
                                        className="form-select form-select-sm"
                                        value={size}
                                        onChange={(e) =>
                                            handleSizeChange(parseInt(e.target.value, 10))
                                        }
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column p-0">
                        {loading ? (
                            <div className="ecx-page-empty py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : campaigns.length === 0 ? (
                            <div className="ecx-page-empty py-5">
                                <span className="ecx-page-empty__icon">
                                    <i className="bi bi-clock-history" />
                                </span>
                                <h5 className="mb-2 text-dark">{emptyTitle}</h5>
                                <p className="mb-3 text-muted">{emptyDescription}</p>
                                {createPath && (
                                    <Link to={createPath} className="btn btn-primary btn-sm">
                                        <i className="bi bi-plus-lg me-1" />
                                        {createLabel}
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive ecx-past-blast__table-wrap">
                                    <table className="table table-hover align-middle mb-0 ecx-past-blast__table">
                                        <thead>
                                            <tr>
                                                <th className="ecx-past-blast__col-blast ps-3">
                                                    {entityLabel}
                                                </th>
                                                {isSms && (
                                                    <th className="ecx-past-blast__col-message">
                                                        Message
                                                    </th>
                                                )}
                                                <th className="ecx-past-blast__col-created">
                                                    Created
                                                </th>
                                                <th className="ecx-past-blast__col-status">
                                                    Status
                                                </th>
                                                <th className="ecx-past-blast__col-actions pe-3 text-end">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign) => {
                                                const statusMeta =
                                                    STATUS_META[campaign.status] || {
                                                        label: campaign.status || 'Unknown',
                                                        className: 'is-unknown',
                                                        icon: 'bi-circle',
                                                        hint: '',
                                                    };
                                                const showResults =
                                                    resultsBasePath &&
                                                    canExploreResults(campaign.status);
                                                const showStop = canStopCampaign(campaign.status);
                                                const isStopping = stoppingId === campaign.id;

                                                return (
                                                    <tr key={campaign.id}>
                                                        <td className="ps-3">
                                                            <div className="fw-normal text-dark">
                                                                {campaign.name ||
                                                                    `Untitled ${entityLower}`}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {campaign.description
                                                                    ? truncateText(
                                                                          campaign.description,
                                                                          80
                                                                      )
                                                                    : 'No description'}
                                                            </div>
                                                        </td>
                                                        {isSms && (
                                                            <td>
                                                                {campaign.contentPath ? (
                                                                    <span className="ecx-past-blast__message-text">
                                                                        {truncateText(
                                                                            campaign.contentPath,
                                                                            110
                                                                        )}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">
                                                                        —
                                                                    </span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="text-muted text-nowrap">
                                                            {campaign.createdAt || '—'}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className={`ecx-past-blast__status ${statusMeta.className}`}
                                                                title={statusMeta.hint}
                                                            >
                                                                <i
                                                                    className={`bi ${statusMeta.icon}${
                                                                        campaign.status ===
                                                                        'IN_PROGRESS'
                                                                            ? ' ecx-refresh-spin'
                                                                            : ''
                                                                    }`}
                                                                />
                                                                {statusMeta.label}
                                                            </span>
                                                        </td>
                                                        <td className="pe-3 text-end">
                                                            <div className="ecx-past-blast__actions">
                                                                {showStop && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() =>
                                                                            handleStopCampaign(
                                                                                campaign
                                                                            )
                                                                        }
                                                                        disabled={Boolean(stoppingId)}
                                                                        aria-busy={isStopping}
                                                                    >
                                                                        {isStopping ? (
                                                                            <>
                                                                                <span
                                                                                    className="spinner-border spinner-border-sm me-1"
                                                                                    role="status"
                                                                                    aria-hidden="true"
                                                                                />
                                                                                Stopping
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <i className="bi bi-stop-circle me-1" />
                                                                                Stop
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                )}
                                                                {showResults && (
                                                                    <Link
                                                                        to={`${resultsBasePath}/${campaign.id}/results`}
                                                                        className="btn btn-sm btn-outline-primary ecx-past-blast__results-btn"
                                                                    >
                                                                        <i className="bi bi-bar-chart me-1" />
                                                                        Results
                                                                    </Link>
                                                                )}
                                                                {!showStop && !showResults && (
                                                                    <span
                                                                        className="text-muted small"
                                                                        title="Stop is available while pending; results after complete or error"
                                                                    >
                                                                        —
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top p-3 mt-auto">
                                        <Pagination
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={size}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PastBlastList;
